import React, { useState, useEffect } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { presaleApi, Presale } from '../services/api';
import { StickyHeader, DesktopNav, DesktopPhonePill, BrandLogo } from '../design-system/components';
import './PresalePage.css';

const PresalePage: React.FC = () => {
  const [presales, setPresales] = useState<Presale[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPresale, setSelectedPresale] = useState<Presale | null>(null);
  const [filter, setFilter] = useState('all'); // all, upcoming, ongoing

  useEffect(() => {
    loadPresales();
  }, []);

  const loadPresales = async () => {
    try {
      setLoading(true);
      // Backend API 호출
      const response = await presaleApi.getList();
      const presalesList = response.results || response;
      setPresales(presalesList);
    } catch (error) {
      console.error('Failed to load presales:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredPresales = () => {
    const now = new Date();

    if (filter === 'upcoming') {
      return presales.filter(p => p.supply_date && new Date(p.supply_date) > now);
    } else if (filter === 'ongoing') {
      return presales.filter(p => p.supply_date && new Date(p.supply_date) <= now);
    }
    return presales;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
  };

  const getDDay = (dateString?: string) => {
    if (!dateString) return null;
    const target = new Date(dateString);
    const now = new Date();
    const diff = Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (diff > 0) return `D-${diff}`;
    if (diff === 0) return 'D-Day';
    return '분양 완료';
  };

  useSeoMeta({ robots: 'noindex, nofollow' });

  return (
    <div className="presale-page">
      {/* Phase 7: lg+ 데스크탑 통일 헤더 — 다른 페이지와 nav/문의 통일 */}
      <div className="hidden lg:block bg-paper">
        <div className="mx-auto max-w-[1240px]">
          <StickyHeader
            left={
              <div className="flex items-center gap-6">
                <BrandLogo />
                <DesktopNav />
              </div>
            }
            right={<DesktopPhonePill />}
          />
        </div>
      </div>
      {/* Hero Section */}
      <div className="presale-hero">
        <div className="hero-overlay">
          <div className="hero-content">
            <h1>분양 정보</h1>
            <p>새로운 보금자리를 찾아보세요</p>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="filter-tabs">
        <button
          className={`tab ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          전체
        </button>
        <button
          className={`tab ${filter === 'upcoming' ? 'active' : ''}`}
          onClick={() => setFilter('upcoming')}
        >
          분양 예정
        </button>
        <button
          className={`tab ${filter === 'ongoing' ? 'active' : ''}`}
          onClick={() => setFilter('ongoing')}
        >
          분양 중
        </button>
      </div>

      {/* Presale List */}
      <div className="presale-container">
        <div className="presale-header">
          <h2>분양 단지</h2>
          <span className="count">{getFilteredPresales().length}개</span>
        </div>

        {loading ? (
          <div className="loading">로딩 중...</div>
        ) : (
          <div className="presale-grid">
            {getFilteredPresales().map((presale) => {
              const dday = getDDay(presale.supply_date);
              return (
                <div
                  key={presale.id}
                  className="presale-card"
                  onClick={() => setSelectedPresale(presale)}
                >
                  <div className="presale-image">
                    {presale.image_url ? (
                      <img src={presale.image_url} alt={presale.name} />
                    ) : (
                      <div className="no-image">이미지 없음</div>
                    )}
                    {dday && (
                      <div className={`dday-badge ${dday === '분양 완료' ? 'completed' : ''}`}>
                        {dday}
                      </div>
                    )}
                  </div>

                  <div className="presale-info">
                    <div className="brand">{presale.brand}</div>
                    <h3>{presale.name}</h3>
                    <p className="theme">{presale.theme}</p>
                    <p className="address">{presale.address}</p>
                    <div className="supply-date">
                      <span className="label">분양 일정</span>
                      <span className="date">{formatDate(presale.supply_date)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedPresale && (
        <div className="modal-overlay" onClick={() => setSelectedPresale(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close"
              onClick={() => setSelectedPresale(null)}
            >
              ×
            </button>

            <div className="modal-body">
              <div className="modal-image">
                {selectedPresale.image_url ? (
                  <img src={selectedPresale.image_url} alt={selectedPresale.name} />
                ) : (
                  <div className="no-image-large">이미지 없음</div>
                )}
              </div>

              <div className="modal-info">
                <div className="brand-tag">{selectedPresale.brand}</div>
                <h2>{selectedPresale.name}</h2>
                <p className="theme-detail">{selectedPresale.theme}</p>

                <div className="detail-section">
                  <h4>위치</h4>
                  <p>{selectedPresale.address}</p>
                </div>

                <div className="detail-section">
                  <h4>단지 소개</h4>
                  <p>{selectedPresale.description}</p>
                </div>

                <div className="detail-section">
                  <h4>분양 일정</h4>
                  <p className="supply-date-large">{formatDate(selectedPresale.supply_date)}</p>
                  {getDDay(selectedPresale.supply_date) && (
                    <p className="dday-large">{getDDay(selectedPresale.supply_date)}</p>
                  )}
                </div>

                <div className="modal-actions">
                  <button className="interest-btn">관심 단지 등록</button>
                  <button className="inquiry-btn">문의하기</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PresalePage;
